'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ParamsObject } from '@/frontastic/tastics/types';
import {
  objectToQueryString,
  queryStringToObject,
  formatPhoneNumber,
  getSelectedDivision,
  getCurrentDateTime,
} from '@/helpers/utils/siteUtils';
import useSiteTranslation from '@/providers/i18n/hooks/useSiteTranslation';
import {
  SEARCH_CUSTOMER_PAGE_PATH,
  SEARCH_ORDER_PAGE_PATH,
  PAGE_TEXTS_KEYS,
  defaultDurationInMonths,
} from '../../../../../../constants/constant';
import { PAYLOAD_QUERY } from '../../../customer-detail/constants';
import { Tabs, TabsList, TabsTrigger } from '../../../ui/tabs';
import { CUSTOMER_SEARCH_TYPES, ORDER_SEARCH_TYPES } from '../../constants';
import SearchCustomer from '../customers';
import SearchOrder from '../orders';

function Index({
  pagePath,
  setDataFetchInProgress,
  onTabChange,
  setAllRowsDataRef,
}: Readonly<{
  pagePath: string;
  setDataFetchInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  onTabChange?: (event: string) => void;
  setAllRowsDataRef?: any;
}>) {
  const router = useRouter();
  const Texts = useSiteTranslation(PAGE_TEXTS_KEYS?.customerAndOrderSearch);
  const [searchParams, setSearchParams] = useState({});
  const [selectedSearchType, setSelectedSearchType] = useState<string>(
    pagePath === SEARCH_CUSTOMER_PAGE_PATH ? CUSTOMER_SEARCH_TYPES?.customerIdentifier : ORDER_SEARCH_TYPES?.orderId,
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const paramObject = queryStringToObject(window.location.search.substring(1)) || {};
      setSearchParams(paramObject);
      if (Object.keys(paramObject)?.length > 0) {
        const selectedSearchTypeQueryParam = queryStringToObject(
          window.location.search.substring(1),
        )?.selectedSearchType;
        if (selectedSearchTypeQueryParam) {
          setSelectedSearchType(selectedSearchTypeQueryParam);
        }
      }
    }
  }, []);

  const handleSearch = (params: ParamsObject) => {
    setAllRowsDataRef.current = null;
    const urlParams = new URLSearchParams(window.location.search);
    const selectedDivision = urlParams.get(PAYLOAD_QUERY?.DIVISION_CODE) || getSelectedDivision();
    if (pagePath === SEARCH_ORDER_PAGE_PATH && params?.zip) {
      params.zipCode = params.zip;
      delete params.zip;
    }
    const currentTimeStamp = getCurrentDateTime();
    const newSearchParams = {
      ...params,
      ...(params.phoneNumber ? { phoneNumber: formatPhoneNumber(params.phoneNumber) } : {}),
      ...(selectedDivision ? { divisionCode: selectedDivision } : {}),
      ...(pagePath === SEARCH_ORDER_PAGE_PATH
        ? { durationInMonths: defaultDurationInMonths, isExternalOrderId: params?.orderId ? 'false' : 'true' }
        : {}),
      initiateSearch: true,
      selectedSearchType: selectedSearchType,
      timeStamp: currentTimeStamp,
    };
    setDataFetchInProgress(true);
    const queryString = objectToQueryString(newSearchParams);
    router.push(`/${pagePath}?${queryString}`);
    setSearchParams(newSearchParams);
  };
  return (
    <section className="relative">
      <Tabs
        defaultValue={pagePath}
        onValueChange={(value) => {
          onTabChange && onTabChange(value);
        }}
      >
        <section className="mb-4 border-b border-[#F0F0F0] pt-3">
          <TabsList>
            <TabsTrigger
              value={SEARCH_CUSTOMER_PAGE_PATH}
              className="mr-5 px-0 pb-3 text-lg font-normal text-[#656668]"
            >
              {Texts?.customerSearchTabHeading}
            </TabsTrigger>
            <TabsTrigger value={SEARCH_ORDER_PAGE_PATH} className="px-0 pb-3 text-lg font-normal text-[#656668]">
              {Texts?.orderSearchTabHeading}
            </TabsTrigger>
          </TabsList>
        </section>
        {pagePath === SEARCH_CUSTOMER_PAGE_PATH && (
          <SearchCustomer
            setSelectedSearchType={setSelectedSearchType}
            selectedSearchType={selectedSearchType}
            key={selectedSearchType}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            handleSearch={handleSearch}
          />
        )}
        {pagePath === SEARCH_ORDER_PAGE_PATH && (
          <SearchOrder
            setSelectedSearchType={setSelectedSearchType}
            selectedSearchType={selectedSearchType}
            key={selectedSearchType}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            handleSearch={handleSearch}
          />
        )}
      </Tabs>
    </section>
  );
}
export default Index;
